<template>
  <div class="teams">
    <div class="page-header">
      <h1>College Football Teams</h1>
      <div class="search-container">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search teams..."
          class="search-input"
        />
      </div>
    </div>

    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading teams...</p>
    </div>

    <div v-else-if="filteredTeams.length === 0" class="no-teams">
      <i class="fas fa-search"></i>
      <p>No teams found matching your search</p>
    </div>

    <div v-else class="teams-container">
      <div
        v-for="team in filteredTeams"
        :key="team.id"
        class="team-card"
        @click="goToTeamDetail(team.id)"
      >
        <div class="team-logo">
          <img
            v-if="team.logo"
            :src="team.logo"
            :alt="team.short_display_name || team.display_name"
            @error="handleImageError"
          />
          <div v-else class="logo-placeholder">
            {{ getTeamInitial(team) }}
          </div>
        </div>
        <div class="team-info">
          <div class="team-name">
            {{ team.display_name }}
            <button
              class="favorite-btn"
              :class="{
                'is-favorite': favoriteTeams.some((fav) => fav.id === team.id),
              }"
              @click.stop="toggleFavoriteTeam(team.id, team.display_name)"
              :title="
                favoriteTeams.some((fav) => fav.id === team.id)
                  ? 'Remove from favorites'
                  : 'Add to favorites'
              "
            >
              <i class="fas fa-star"></i>
            </button>
          </div>
          <div class="team-abbr">{{ team.abbrev }}</div>
          <div v-if="team.rank && team.rank < 99" class="team-rank">
            #{{ team.rank }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cookieUtils } from "../utils/cookieUtils";
import { getTeamInitial, handleImageError, transformTeamData } from "../utils/teamUtils";

export default {
  name: "TeamsView",
  data() {
    return {
      teams: [],
      searchQuery: "",
      isLoading: false,
      favoriteTeams: [],
    };
  },
  computed: {
    filteredTeams() {
      if (!this.searchQuery) return this.teams;

      const query = this.searchQuery.toLowerCase();
      return this.teams.filter((team) => {
        const displayName = (team.display_name || "").toLowerCase();
        const shortDisplayName = (team.short_display_name || "").toLowerCase();
        const abbrev = (team.abbrev || "").toLowerCase();

        return (
          displayName.includes(query) ||
          shortDisplayName.includes(query) ||
          abbrev.includes(query)
        );
      });
    },
  },
  created() {
    this.fetchTeams();
  },
  methods: {
    getTeamInitial,
    handleImageError,
    async fetchTeams() {
      this.isLoading = true;
      try {
        // Use the teams data service
        const { default: dataService } = await import("../utils/dataService");
        const teamsData = await dataService.loadTeamsData();

        // Transform teams data to match component expectations
        this.teams = teamsData.map(transformTeamData);

        // Fetch rankings data to enhance team information
        try {
          const rankingsData = await dataService.loadRankingsData();
          if (rankingsData && rankingsData.rankings) {
            // Create a map of teamId to ranking for quick lookup
            const rankingsMap = {};
            rankingsData.rankings.forEach(poll => {
              poll.ranks.forEach(team => {
                if (!rankingsMap[team.teamId]) {
                  rankingsMap[team.teamId] = {
                    rank: team.rank,
                    poll: poll.poll,
                    points: team.points
                  };
                }
              });
            });

            // Enhance teams with ranking information
            this.teams = this.teams.map(team => {
              const ranking = rankingsMap[team.id];
              return {
                ...team,
                rank: ranking ? ranking.rank : 99
              };
            });
          }
        } catch (error) {
          console.warn("Failed to fetch rankings data:", error);
        }

        // Filter out any invalid teams first
        this.teams = this.teams.filter(team => team && team.id);
        // Sort teams by name (with null check)
        this.teams.sort((a, b) => {
          const nameA = a.display_name || a.short_display_name || a.abbrev || "Unknown";
          const nameB = b.display_name || b.short_display_name || b.abbrev || "Unknown";
          return nameA.localeCompare(nameB);
        });
        // Load favorite teams from cookie
        this.favoriteTeams = cookieUtils.getFavoriteTeams();
      } catch (error) {
        console.error("Error fetching teams:", error);
        this.teams = [];
      } finally {
        this.isLoading = false;
      }
    },
    goToTeamDetail(teamId) {
      // This would navigate to a team detail page
      // For now, we'll just log the team ID
      console.log(`Navigate to team detail for ID: ${teamId}`);
    },
    toggleFavoriteTeam(teamId, teamName) {
      if (cookieUtils.isFavoriteTeam(teamId)) {
        cookieUtils.removeFavoriteTeam(teamId);
        this.favoriteTeams = this.favoriteTeams.filter(
          (team) => team.id !== teamId
        );
      } else {
        cookieUtils.addFavoriteTeam(teamId, teamName);
        this.favoriteTeams = cookieUtils.getFavoriteTeams();
      }
    },
  },
};
</script>

<style scoped>
.teams {
  width: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #30363d;
}

.page-header h1 {
  color: #c9d1d9;
  font-size: 2rem;
}

.search-container {
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #30363d;
  border-radius: 4px;
  font-size: 1rem;
  background-color: #0d1117;
  color: #e0e0e0;
  transition: border-color 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #58a6ff;
}

.loading,
.no-teams {
  text-align: center;
  padding: 3rem;
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border: 1px solid #30363d;
}

.loading i,
.no-teams i {
  font-size: 3rem;
  color: #ff7b45;
  margin-bottom: 1rem;
}

.teams-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  width: 100%;
}

.team-card {
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  padding: 1.25rem;
  border: 1px solid #30363d;
  min-height: 80px;
}

.team-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
}

.team-logo {
  width: 50px;
  height: 50px;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.team-logo img {
  max-width: 100%;
  max-height: 100%;
}

.logo-placeholder {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #4a5568, #2d3748);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 20px;
  color: #e2e8f0;
  text-transform: uppercase;
}

.team-info {
  flex: 1;
}

.team-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.favorite-btn {
  background: none;
  border: none;
  color: #8b949e;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: 0.75rem;
  flex-shrink: 0;
}

.favorite-btn:hover {
  color: #ffd43b;
  background-color: rgba(255, 212, 59, 0.1);
}

.favorite-btn.is-favorite {
  color: #ffd43b;
}

.team-abbr {
  font-size: 0.875rem;
  color: #8b949e;
  text-transform: uppercase;
}

.team-rank {
  display: inline-block;
  background-color: transparent;
  color: #8b949e;
  font-size: 0.7rem;
  font-weight: 400;
  padding: 0;
  border-radius: 0;
  margin-top: 0.25rem;
  opacity: 0.8;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-container {
    margin-top: 1rem;
    width: 100%;
  }

  .teams-container {
    grid-template-columns: 1fr;
  }
}
</style>
